import React from 'react';
import { RedirectState } from 'src/domains/casino/state/AppCasino/Redirect.state';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import confirmation from 'src/utils/confirm.jsx';
import { casinoMiniGamesCategoryName, getDropdownOptions } from 'src/domains/casino/utils/utils';
import { CategoriesAndGamesCollection } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { CategoryType, GameType, PRAGMATIC_NAME } from 'src/domains/casino/models/models';
import { GameImageUploaderState } from 'src/domains/casino/state/AppCasino/game-image-uploader/GameImageUploader.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

interface FormType {
    gameName: string;
    gameID: string;
    gameRectangleImage: string;
    gameRectangleVerticalImage: string;
    gameSquareImage: string;
    categories: Array<string>;
    isDemo: boolean;
    maxBet: string;
    minBet: string;
    maxWin: string;
    returnToPlayer: string;
    jackpotAmount: string;
    description: string;
}

export class GameState {
    @observable public isLoading: boolean = false;
    @observable public isPublished: boolean = this.game.published;

    public readonly rectangleImageUploader: GameImageUploaderState;
    public readonly rectangleVerticalImageUploader: GameImageUploaderState;
    public readonly squareImageUploader: GameImageUploaderState;

    public readonly gameName: FormInputState<string, string>;
    public readonly gameIdInput: FormInputState<string, string>;
    public readonly categories: FormInputState<Array<string>, Array<string>>;
    public readonly categoryOptions: Record<string, string>;
    public readonly isDemo: FormInputState<boolean, boolean>;
    public readonly maxBet: FormInputState<string, string>;
    public readonly minBet: FormInputState<string, string>;
    public readonly maxWin: FormInputState<string, string>;
    public readonly returnToPlayer: FormInputState<string, string>;
    public readonly jackpotAmount: FormInputState<string, string>;
    public readonly description: FormInputState<string, string>;
    public readonly gameRectangleImage: FormInputState<string, string>;
    public readonly gameRectangleVerticalImage: FormInputState<string, string>;
    public readonly gameSquareImage: FormInputState<string, string>;

    private readonly form: FormModel<FormType>;

    public constructor(
        public readonly gameId: number,
        private readonly collection: CategoriesAndGamesCollection,
        private readonly redirectState: RedirectState,
        private readonly common: Common
    ) {
        makeAutoObservable(this);
        const availableCategories = getDropdownOptions(this.game, this.collection);
        const availableCategoriesFiltered =
            this.game.provider === PRAGMATIC_NAME
                ? availableCategories
                : availableCategories.filter((category) => category.name !== casinoMiniGamesCategoryName);
        this.categoryOptions = this.getCategoriesMap(availableCategoriesFiltered);

        this.rectangleImageUploader = new GameImageUploaderState(this.game.image);
        this.rectangleVerticalImageUploader = new GameImageUploaderState(this.game.image_vertical);
        this.squareImageUploader = new GameImageUploaderState(this.game.image_square);

        const gameID = FormInputState.new(this.gameId.toString());

        this.gameName = FormInputState.new(this.game.name);
        this.gameIdInput = FormInputState.new(this.game.id.toString());
        this.categories = FormInputState.new(this.categoriesGameBelongsTo);
        this.isDemo = FormInputState.new(this.game.demo);
        this.maxBet = FormInputState.new(this.game.max_bet.toString());
        this.minBet = FormInputState.new(this.game.min_bet.toString());
        this.maxWin = FormInputState.new(this.game.max_win.toString());
        this.returnToPlayer = FormInputState.new(this.game.return_to_player.toString());
        this.jackpotAmount = FormInputState.new('');
        this.description = FormInputState.new(this.game.description);
        this.gameRectangleImage = FormInputState.new(this.game.image);
        this.gameRectangleVerticalImage = FormInputState.new(this.game.image_vertical);
        this.gameSquareImage = FormInputState.new(this.game.image_square);

        this.form = FormModel.group({
            gameName: this.gameName,
            gameID,
            gameRectangleImage: this.gameRectangleImage,
            gameRectangleVerticalImage: this.gameRectangleVerticalImage,
            gameSquareImage: this.gameSquareImage,
            categories: this.categories,
            isDemo: this.isDemo,
            maxBet: this.maxBet,
            minBet: this.minBet,
            maxWin: this.maxWin,
            returnToPlayer: this.returnToPlayer,
            jackpotAmount: this.jackpotAmount,
            description: this.description,
        });

        // eslint-disable-next-line
        this.rectangleImageUploader.readImageSize();
        // eslint-disable-next-line
        this.rectangleVerticalImageUploader.readImageSize();
        // eslint-disable-next-line
        this.squareImageUploader.readImageSize();
    }

    public get game(): GameType {
        const game = this.collection.games.find((game) => game.id === this.gameId) ?? null;

        if (game === null) {
            throw Error;
        }

        return game;
    }

    public get isGamePublished(): boolean {
        return this.isPublished;
    }

    public publishGame = async (): Promise<void> => {
        this.isPublished = true;
        await this.setGameStatus(true);
    };

    public unpublishGame = async (): Promise<void> => {
        this.isPublished = false;
        await this.setGameStatus(false);
    };

    @computed public get categoriesGameBelongsTo(): Array<string> {
        const categories: Array<string> = [];
        this.collection.categories.forEach((category) => {
            if (category.game_ids.includes(this.game.id)) {
                categories.push(category.id.toString());
            }
        });
        return categories;
    }

    public gameHasSelectedCategoryName = (categoryName: string): boolean => {
        let hasSelectedCategory = false;
        this.collection.categories.forEach((category) => {
            if (category.name === categoryName && category.game_ids.includes(this.game.id)) {
                hasSelectedCategory = true;
            }
        });
        return hasSelectedCategory;
    };

    @action public setGameStatus = async (isPublished: boolean): Promise<void> => {
        this.isLoading = true;
        const { editGame } = this.collection;
        const {
            id,
            demo,
            description,
            image,
            image_vertical,
            image_square,
            jackpot_amount,
            max_bet,
            min_bet,
            max_win,
            return_to_player,
        } = this.game;

        await editGame({
            gameId: id,
            gameDetails: {
                published: isPublished,
                demo,
                description,
                image,
                image_vertical,
                image_square,
                jackpot_amount,
                max_bet,
                min_bet,
                max_win,
                return_to_player,
            },
        });

        this.isLoading = false;
    };

    @action public handleDeleteGame = async (): Promise<void> => {
        const { deleteGame } = this.collection;
        const result = await confirmation('Are you sure you want to delete this game?', 'ok');

        //@ts-expect-error
        if (result === 'ok') {
            await deleteGame(this.game.id);
        }
        this.isLoading = false;

        this.redirectState.redirectToCollection(this.collection.collection);
    };

    @action public resetForm = (): void => {
        this.form.reset();
    };

    @action public handleSubmit = async (): Promise<void> => {
        const { editGame, addGameToCategory, deleteGameFromCategory } = this.collection;
        const popupsStack = PopupsStackState.get(this.common);
        const { casinoStoreEnabled } = ConfigState.get(this.common).config;

        this.form.setAsVisited();
        const { result } = this.form;

        if (this.categories.value.length > 0 && result.value.type === 'ok') {
            this.isLoading = true;

            const rectangleImageResult = await this.rectangleImageUploader.uploadImage(this.collection.collection);
            const rectangleVerticalImageResult = await this.rectangleVerticalImageUploader.uploadImage(
                this.collection.collection
            );
            const squareImageResult = await this.squareImageUploader.uploadImage(this.collection.collection);

            if (
                rectangleImageResult === 'upload_error' ||
                rectangleVerticalImageResult === 'upload_error' ||
                squareImageResult === 'upload_error'
            ) {
                popupsStack.addPopup('casino_upload_fail', {
                    template: 'generic',
                    hasOverlay: true,
                    title: 'Error',
                    message: <p className='m-0'>Image upload failed. Please try again.</p>,
                    confirm_label: 'Confirm',
                    onConfirm: () => {
                        popupsStack.closePopupOnTop();
                    },
                    onCancel: () => {
                        popupsStack.closePopupOnTop();
                    },
                });

                this.isLoading = false;
                return;
            }

            const { isDemo, maxBet, minBet, maxWin, returnToPlayer, jackpotAmount, description, categories } =
                result.value.data;
            await editGame({
                gameId: this.game.id,
                gameDetails: {
                    published: this.game.published,
                    image: this.rectangleImageUploader.imagePreview,
                    image_vertical: this.rectangleVerticalImageUploader.imagePreview,
                    image_square: this.squareImageUploader.imagePreview,
                    demo: isDemo,
                    description: description,
                    max_bet: maxBet,
                    min_bet: minBet,
                    max_win: maxWin,
                    jackpot_amount: jackpotAmount,
                    return_to_player: returnToPlayer,
                },
            });

            const categoriesGameBelongsToSorted = this.categoriesGameBelongsTo
                .map((id) => parseInt(id, 10))
                .slice()
                .sort((a, b) => a - b);

            const isCategoriesArrayUnchanged =
                categories.length === this.categoriesGameBelongsTo.length &&
                categories
                    .map((id) => parseInt(id, 10))
                    .slice()
                    .sort((a, b) => a - b)
                    .every(function (value, index) {
                        return value === categoriesGameBelongsToSorted[index];
                    });

            if (isCategoriesArrayUnchanged === false) {
                for (const categoryId of categories) {
                    const gameIsNotInCategory = !this.categoriesGameBelongsTo.includes(categoryId);
                    const categoriesLimitIsNotReached = categories.length <= 5;
                    if (gameIsNotInCategory && categoriesLimitIsNotReached) {
                        if (casinoStoreEnabled) {
                            // TODO: change request name after refactor this method (remove old casino cms)
                            await deleteGameFromCategory(this.game.id, categories);
                        } else {
                            await addGameToCategory(this.game.id, categoryId);
                        }
                    }
                }
            }
            const categoriesRemoved = this.categoriesGameBelongsTo.filter((val) => !categories.includes(val));

            if (categoriesRemoved.length > 0) {
                await deleteGameFromCategory(this.game.id, casinoStoreEnabled ? categories : categoriesRemoved);
            }

            this.isLoading = false;
        }
    };

    private getCategoriesMap = (categories: Array<CategoryType>): Record<string, string> => {
        const result: Record<string, string> = {};
        categories.forEach((category) => {
            result[category.id] = category.name;
        });
        return result;
    };
}
